import { Observable, of, race, SchedulerLike } from 'rxjs';
import { catchError, delay, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

export type ProgressStatus = 'acquiring' | 'failed_to_acquire' | 'partial' | 'full';

export interface Progress {
  expectedItems: number;
  processedItems: number;
  isFinalized: boolean;
  status: ProgressStatus;
}

export const ACQUIRING: Progress = {
  processedItems: 0,
  expectedItems: 100,
  status: 'acquiring',
  isFinalized: false,
};
export const FAILED: Progress = {
  processedItems: 0,
  expectedItems: 1,
  status: 'failed_to_acquire',
  isFinalized: false,
};

export interface ProgressInputs {
  processed: number;
  expected: number;
  isFinalized: boolean;
}

// Subject-like object which coerces streams to produce UI-friendly progress values
export class ProgressSubject {
  private readonly delegate$: Observable<Progress>;

  constructor(inputs$: Observable<ProgressInputs>, sched?: SchedulerLike, statusWaitLimit = 5000, log = true) {
    inputs$ = inputs$.pipe(
      map((v) => {
        v.processed = v.processed === undefined ? -1 : v.processed;
        v.expected = v.expected === undefined ? -1 : v.expected;
        return v;
      }),
    );
    const processedProgress$ = inputs$.pipe(
      map<ProgressInputs, Progress>((i) => {
        if (i.processed > -1 && i.expected > -1) {
          return {
            status: 'full',
            processedItems: i.processed,
            expectedItems: i.expected,
            isFinalized: i.isFinalized,
          };
        }
        return {
          status: 'partial',
          processedItems: i.processed,
          expectedItems: i.expected,
          isFinalized: i.isFinalized,
        };
      }),
      catchError((err) => {
        if (log) {
          console.error(err);
        }
        return of<Progress>({
          status: 'failed_to_acquire',
          processedItems: 0,
          expectedItems: 1,
          isFinalized: false,
        });
      }),
      // TODO: Consider using publishReplay to avoid excessive memory usage
      shareReplay(1),
    );
    const failAfterDelay$ = of('').pipe(
      delay(statusWaitLimit, sched || undefined),
      switchMap(() => processedProgress$.pipe(startWith<Progress>(FAILED))),
    );
    this.delegate$ = race([failAfterDelay$, processedProgress$]).pipe(
      startWith<Progress>(ACQUIRING),
      // TODO: Consider using publishReplay to avoid excessive memory usage
      shareReplay(1),
    );
  }

  public asObservable(): Observable<Progress> {
    return this.delegate$;
  }
}
